import { DateTime } from "luxon";
import {
  Constraint,
  Identifiable,
  OptimizedGantt,
  Project,
  ProjectElementType,
} from "@planzone/dto";
import React from "react";

function isBlocked(
  element: OptimizedGantt[number],
  constraints: (Identifiable<Constraint> & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  })[],
) {
  return !!constraints.find(
    ({ targetEntityUuid, originDateType, targetDateType, originIsDone }) =>
      targetEntityUuid === element.uuid &&
      originDateType === "END" &&
      targetDateType === "START" &&
      !originIsDone,
  );
}

// function isOpen(
//   projectElement: OptimizedGantt[number],
//   toggledLists: { [key: string]: boolean },
// ) {
//   const isList = projectElement.type === ProjectElementType.LIST;
//   const isProject = projectElement.type === ProjectElementType.PROJECT;
//
//   return isProject
//     ? typeof toggledLists[projectElement.uuid] === "undefined"
//       ? true
//       : toggledLists[projectElement.uuid]
//     : isList
//       ? typeof toggledLists[projectElement.uuid] === "undefined"
//         ? true
//         : toggledLists[projectElement.uuid]
//       : false;
// }

function renderWithTreeCtx(
  gantt: OptimizedGantt,
  project: Identifiable<Project>,
  isOpen: (id: string) => boolean,
  render: (
    projectElement: OptimizedGantt[number],
    ctx: { minDate: string; maxDate: string },
    index: number,
    fullCtx: {
      [key: number]: { minDate: string; maxDate: string };
    },
    breadcrumbs: OptimizedGantt,
  ) => React.JSX.Element,
): React.ReactNode[] {
  return gantt.reduce<{
    ui: React.JSX.Element[];
    filteredCount: number;
    ctx: {
      [key: number]: {
        minDate: string;
        maxDate: string;
        filtered: boolean;
      };
    };
    breadcrumbs: OptimizedGantt;
  }>(
    ({ ui, ctx, filteredCount, breadcrumbs }, projectElement, index) => {
      const isChangingContext =
        projectElement.type === ProjectElementType.PROJECT;
      const isChangingDepth =
        projectElement.type === ProjectElementType.LIST &&
        gantt[index + 1]?.depth !== projectElement.depth;
      const filtered = ctx[projectElement.depth - 1]?.filtered;

      return {
        filteredCount: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? filteredCount
          : filteredCount + 1,
        ui: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? [
              ...ui,
              render(
                projectElement,
                ctx[
                  projectElement.depth - 1 > 0 ? projectElement.depth - 1 : 0
                ],
                index,
                ctx,
                breadcrumbs,
              ),
            ]
          : ui,
        ctx: isChangingContext
          ? {
              0: {
                minDate: projectElement.startDate,
                maxDate: projectElement.endDate,
                filtered:
                  !projectElement.startDate ||
                  !projectElement.endDate ||
                  !isOpen(projectElement.uuid),
              },
            }
          : isChangingDepth
            ? {
                ...ctx,
                [projectElement.depth]: {
                  filtered: filtered || !isOpen(projectElement.uuid),
                  minDate:
                    ctx[projectElement.depth]?.minDate ||
                    ctx[projectElement.depth - 1]?.minDate ||
                    "",
                  maxDate:
                    projectElement.endDate ||
                    ctx[projectElement.depth]?.maxDate ||
                    ctx[projectElement.depth - 1]?.maxDate ||
                    "",
                },
              }
            : ctx,
        breadcrumbs: isChangingContext
          ? [projectElement]
          : !!gantt[index + 1]
            ? gantt[index + 1].depth < projectElement.depth
              ? [
                  ...breadcrumbs.slice(
                    0,
                    breadcrumbs.length -
                      (projectElement.depth - gantt[index + 1].depth),
                  ),
                  ...(projectElement.type === ProjectElementType.LIST
                    ? [projectElement]
                    : []),
                ]
              : gantt[index + 1].depth > projectElement.depth &&
                  projectElement.type === ProjectElementType.LIST
                ? [...breadcrumbs, projectElement]
                : breadcrumbs
            : breadcrumbs,
      };
    },
    {
      filteredCount: 0,
      ui: [],
      ctx: {
        0: {
          minDate: project.rootList.startDate,
          maxDate: project.rootList.endDate,
          filtered: !gantt[0]?.startDate || !gantt[0]?.endDate,
        },
      },
      breadcrumbs: [],
    },
  ).ui;
}

function reCalculateNodeDateRange(
  projectElement: OptimizedGantt[number],
  minDate: string,
  maxDate: string,
) {
  return {
    startDate: projectElement.startDate || minDate || "",
    endDate: projectElement.endDate || maxDate || "",
  };
}

// FIXME: timezone bug — see migration notes (docs/migrations/wbs-date-audit.md).
// Returns the UTC ISO of local midnight of `dt`'s calendar day in `zone`.
// This is intentionally TZ-dependent to mirror the pre-Luxon behavior of
// `new Date(d.getFullYear(), d.getMonth(), d.getDate())`. A separate
// bugfix should reconsider whether UTC or project-TZ should be canonical.
function localMidnightOfDayInZone(dt: DateTime, zone: string): string {
  const local = dt.setZone(zone);
  return (
    DateTime.fromObject(
      { year: local.year, month: local.month, day: local.day },
      { zone },
    )
      .toUTC()
      .toISO() ?? ""
  );
}

function reCalculateLeafDateRange(
  projectElement: OptimizedGantt[number],
  // FIXME: timezone bug — `minDate` is accepted but never consumed.
  // When only minDate is supplied (no own dates, no maxDate), both outputs
  // come out empty. Preserved to match audit findings; see open question
  // #1 in docs/migrations/wbs-date-audit.md.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  minDate: string | null,
  maxDate: string | null,
) {
  const userTimezone = DateTime.local().zoneName;
  const safeMaxDate = maxDate ?? "";

  const endDateDt = projectElement.endDate
    ? DateTime.fromISO(projectElement.endDate, { zone: "utc" })
    : safeMaxDate
      ? DateTime.fromISO(safeMaxDate, { zone: "utc" })
      : null;
  const hasEndDate = !!endDateDt && endDateDt.isValid;

  return {
    startDate: projectElement.startDate
      ? (DateTime.fromISO(projectElement.startDate, { zone: "utc" })
          .toUTC()
          .toISO() ?? "")
      : projectElement.endDate && hasEndDate
        ? localMidnightOfDayInZone(endDateDt!, userTimezone)
        : safeMaxDate && hasEndDate
          ? localMidnightOfDayInZone(endDateDt!, userTimezone)
          : "",
    endDate: hasEndDate ? (endDateDt!.toUTC().toISO() ?? "") : "",
  };
}

function reCalculateDateRange(
  projectElement: OptimizedGantt[number],
  minDate: string,
  maxDate: string,
) {
  return projectElement.type === ProjectElementType.LIST ||
    projectElement.type === ProjectElementType.PROJECT
    ? reCalculateNodeDateRange(projectElement, minDate, maxDate)
    : reCalculateLeafDateRange(projectElement, minDate, maxDate);
}

export { isBlocked, renderWithTreeCtx, reCalculateDateRange };
